/**
 * Who is actually broken when a dashboard widget reports an error.
 *
 * The host collects every `sym.reportError` line the runtime binder raises and used to summarise them all
 * the same way — "Some things on this page didn't load correctly", with a "Copy details" and an "Ask
 * Symphony to fix" hand-off. That sentence is a claim about US, and most of the time it is false: the
 * commonest failure by far is a CONNECTED service that is down or unreachable (a self-hosted MCP server, a
 * connector's upstream API), which the widget already renders its own designed state for ("Could not reach
 * Home Assistant"). Blaming the page for someone else's outage misleads the user, and the hand-off sends
 * the assistant off to "fix" widgets that are not broken.
 *
 * So classify first. An `outage` is an external service failing to answer; a `defect` is anything else —
 * something Symphony could actually fix.
 *
 * ⚠️ BE CONSERVATIVE. A line we cannot confidently place is a `defect`: an extra banner costs a moment of
 * attention, while a real bug quietly re-labelled "the service is down" is a bug nobody ever fixes. That is
 * why an outage needs BOTH halves of the evidence — proof the call left our process for a named external
 * endpoint, AND a transport/upstream failure — rather than a bare "timed out" that any of our own code
 * could equally have produced.
 *
 * Pure + import-free on purpose: it is exercised directly by jest, which cannot load the app's graph.
 */

/** Proof the failure happened on the far side of a call OUT of our process, at a named endpoint. */
const EXTERNAL_CALL = [
  // The tool relay's own shape, e.g. `Error calling ha_list_floors_areas on "http://host:8123": fetch failed`.
  /\bcalling\s+\S+\s+on\s+["'][^"']+["']/i,
  /\bmcp\s+server\b/i,
  /\bupstream\b/i,
  /\bserver seems unreachable\b/i,
];

/** Proof the far side never answered (or answered as a broken gateway). */
const TRANSPORT_FAILURE = [
  /\bfetch failed\b/i,
  /\bnetwork (?:error|request failed)\b/i,
  /\b(?:econnrefused|econnreset|etimedout|enotfound|eai_again|ehostunreach|enetunreach|epipe)\b/i,
  /\bconnection (?:refused|reset|closed)\b/i,
  /\bsocket hang up\b/i,
  /\bgetaddrinfo\b/i,
  /\b(?:unreachable|offline|not reachable)\b/i,
  /\btimed out\b/i,
  /\btimeout\b/i,
  /\b(?:502|503|504)\b/,
  /\bbad gateway\b/i,
  /\bservice unavailable\b/i,
  /\bgateway time-?out\b/i,
];

const matchesAny = (patterns: RegExp[], line: string) => patterns.some((p) => p.test(line));

/** True only when the line proves an external endpoint failed to answer. Everything else is ours. */
export function isServiceOutage(line: string): boolean {
  if (!line) return false;
  return matchesAny(EXTERNAL_CALL, line) && matchesAny(TRANSPORT_FAILURE, line);
}

/**
 * The service to name in the notice, read off the endpoint the relay reports (`on "<url>"`). A URL is
 * reduced to its host — the whole point is to say WHOSE outage this is, and a path or a port says nothing.
 * Returns null when the line names nothing we can show; the caller falls back to unnamed copy rather than
 * inventing a service.
 */
export function outageServiceName(line: string): string | null {
  const match = /\bon\s+["']([^"']+)["']/i.exec(line || '');
  const raw = match?.[1]?.trim();
  if (!raw) return null;
  const host = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? safeHost(raw) : raw.split('/')[0];
  const name = (host || '')
    .replace(/^www\./i, '')
    .replace(/:\d+$/, '')
    .trim();
  return name || null;
}

function safeHost(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}

export interface ClassifiedDashboardErrors {
  /** `outage` only when EVERY collected error is an external service failing — otherwise `defect`. */
  kind: 'outage' | 'defect';
  /** The lines Symphony could actually fix. The copy/hand-off payload carries these and nothing else. */
  defects: string[];
  /** The services named by the outage lines, deduped in arrival order. Empty when none could be named. */
  services: string[];
}

export function classifyDashboardErrors(errors: readonly string[]): ClassifiedDashboardErrors {
  const defects = errors.filter((line) => !isServiceOutage(line));
  const services: string[] = [];
  errors.forEach((line) => {
    if (!isServiceOutage(line)) return;
    const name = outageServiceName(line);
    if (name && !services.includes(name)) services.push(name);
  });
  return { kind: errors.length > 0 && defects.length === 0 ? 'outage' : 'defect', defects, services };
}
